import React, { useEffect, useMemo, useState } from "react";
import { Broadcast, Lock, LockOpen, Trophy, Clock as ClockIcon, Wind, CloudRain } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TeamLogo } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";

/**
 * Compact game card grid.
 *
 * One card per slate game. Cards display matchup, kickoff, spread, O/U,
 * favorite badge, and a lock/unlock toggle. Excluded games are dimmed so
 * users can visually curate the game set they're playing this week.
 *
 * Data flow:
 *   Primary   → /api/data/snapshot .vegas (has spread/total/ITT)
 *   Fallback  → /api/slate/games (always returns every DK slate game,
 *               overlays Vegas fields when available). Guarantees the
 *               grid renders even when the Vegas cache is empty.
 *
 * Exclusion state is stored in localStorage under `wp_excluded_games` as
 * a set of `AWAY@HOME` game_ids. On future sprints this will be piped
 * into the optimizer's `excluded_games` filter — right now the toggle
 * is purely visual + persisted client-side (state.optimize will still
 * consider every player).
 */
const STORAGE_KEY = "wp_excluded_games";

function loadExcluded() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}

function saveExcluded(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
    // Broadcast so listeners (e.g. SundayAutoMode's excluded-games chip)
    // update the moment a user toggles a card — no reload required.
    window.dispatchEvent(new CustomEvent("wp:excluded-games", { detail: { size: set.size } }));
  } catch { /* private mode safe */ }
}

function TeamPill({ code, itt }) {
  // Sprint 60.42 — treat 0.0 as unknown (preseason SGO returns real
  // entries with itt=0 for every team). Rendering "0.0 ITT" implied a
  // scored projection that wasn't there; "— ITT" is honest.
  const hasItt = itt !== null && itt !== undefined && Number(itt) > 0;
  const hot = hasItt && itt >= 24;
  return (
    <div className="flex flex-col items-center gap-1 min-w-[72px]">
      <TeamLogo team={code} size={34} />
      <div className="text-xl font-black tracking-tight leading-none" style={{ color: teamTextColor(code) }}>
        {code || "—"}
      </div>
      {hasItt ? (
        <div className={`text-[10px] mono uppercase tracking-widest ${hot ? "text-[#39FF14] font-bold" : "text-zinc-500"}`}>
          {itt.toFixed(1)} ITT
        </div>
      ) : (
        <div className="text-[10px] mono uppercase tracking-widest text-zinc-700">— ITT</div>
      )}
    </div>
  );
}

function formatKickoff(iso) {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
  } catch { return "TBD"; }
}

/**
 * Sprint 60.68 — Weather impact pill.
 * Renders inline on the game card footer. Show only when the weather
 * signal is meaningful (MEDIUM or HIGH); LOW / NONE / dome games render
 * nothing so the card stays clean. HIGH pulses red to draw the fade eye;
 * MEDIUM sits at amber. Icon flips to CloudRain when precip drives the
 * classification (precip_pct >= 30) so users can tell rain from wind.
 */
function WeatherPill({ weather }) {
  if (!weather) return null;
  const impact = (weather.impact || "NONE").toUpperCase();
  if (impact === "NONE" || impact === "LOW") return null;
  const wind = Number(weather.wind_mph) || 0;
  const precip = Number(weather.precip_pct) || 0;
  const isPrecipDriven = precip >= 30 && precip >= wind * 2;
  const label = isPrecipDriven
    ? `${Math.round(precip)}% PRECIP`
    : `${Math.round(wind)}MPH WIND`;
  const Icon = isPrecipDriven ? CloudRain : Wind;
  const cls = impact === "HIGH"
    ? "bg-red-600/20 text-red-400 border-red-600/60 animate-pulse"
    : "bg-amber-500/15 text-amber-400 border-amber-500/60";
  return (
    <span
      data-testid={`weather-pill-${impact.toLowerCase()}`}
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] mono uppercase tracking-widest ${cls}`}
      title={`${impact} weather impact — ${weather.conditions || ""} · wind ${wind}mph · precip ${precip}%`}
    >
      <Icon size={10} weight="bold" />
      {label}
    </span>
  );
}


function GameCard({ game, excluded, onToggle }) {
  const spread = game.spread;
  // Sprint 60.42 — treat 0.0 as "no line yet" instead of showing "PK".
  // During offseason/preseason SGO returns real entries with spread=0
  // + total=0 for every game. Rendering "PK" implied a genuine pick'em
  // to users who assumed we already had a legit line; "—" is honest.
  const hasSpread = spread !== null && spread !== undefined && Number(spread) !== 0;
  const spreadTxt = !hasSpread
    ? "—"
    : (spread > 0 ? `-${spread}` : `${spread}`);
  const total = game.total;
  const hasTotal = total !== null && total !== undefined && Number(total) > 0;
  const isVegas = game.source && game.source !== "dk";

  return (
    <div
      data-testid={`game-card-${game.game_id}`}
      onClick={onToggle}
      className={`relative border transition-all cursor-pointer select-none px-3 py-3
        ${excluded
          ? "bg-zinc-950 border-zinc-800 opacity-40 hover:opacity-70"
          : "bg-[#0e0e0e] border-zinc-700 hover:border-[#39FF14]/50 hover:bg-[#141414]"}`}
    >
      {/* Lock indicator top-right */}
      <div className="absolute top-2 right-2">
        {excluded
          ? <Lock size={14} className="text-zinc-500" weight="fill" />
          : <LockOpen size={14} className="text-[#39FF14]" weight="fill" />}
      </div>

      {/* Matchup row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <TeamPill code={game.away} itt={game.away_itt} />
        <div className="flex flex-col items-center gap-0.5 flex-1">
          <div className="text-[9px] mono uppercase tracking-widest text-zinc-600">at</div>
          {game.favorite && hasSpread && (
            <div className="flex items-center gap-1 text-[9px] mono uppercase tracking-widest text-[#39FF14]">
              <Trophy size={10} weight="fill" />
              {game.favorite} {spreadTxt}
            </div>
          )}
        </div>
        <TeamPill code={game.home} itt={game.home_itt} />
      </div>

      {/* Info footer */}
      <div className="flex items-center justify-between text-[10px] mono uppercase tracking-widest text-zinc-500 border-t border-zinc-900 pt-2">
        <div className="flex items-center gap-1">
          <ClockIcon size={10} />
          {formatKickoff(game.kickoff)}
        </div>
        <div className="flex items-center gap-2">
          {/* Sprint 60.68 — Weather Impact pill. Rendered ONLY when
              Open-Meteo (or wttr.in fallback) classified this game as
              MEDIUM or HIGH impact. Dome / retractable games skip
              silently. HIGH → red pulse (WIND ≥20mph or PRECIP ≥60%);
              MEDIUM → amber (WIND ≥15mph or PRECIP ≥30%). Users see
              at a glance which games are bad-weather fades — no need
              to open a weather modal. */}
          <WeatherPill weather={game.weather} />
          <span className={hasTotal ? "text-zinc-300" : "text-zinc-700"}>
            O/U {hasTotal ? total : "—"}
          </span>
          {!isVegas && (
            <span className="text-[9px] text-amber-500/70" title="Vegas data pending — showing DK matchup only">
              DK ONLY
            </span>
          )}
          {isVegas && !hasTotal && (
            <span className="text-[9px] text-amber-500/70" title="Vegas O/U hasn't posted for this game yet — preseason lines lock closer to kickoff">
              LINE PENDING
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GameCardGrid() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(() => loadExcluded());

  // Derive `games` up front so the preset useMemos below (and the
  // handlers that reference it) don't hit a Temporal Dead Zone error.
  const games = payload?.games || [];

  const load = async () => {
    setLoading(true);
    try {
      const p = await api.slateGames();
      setPayload(p);
    } catch (e) {
      console.debug("slateGames load failed:", e?.message || e);
      setPayload({ games: [], source: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Re-fetch when a new slate is applied.
    const onPoolRefreshed = () => load();
    // Sync excluded set with localStorage when another component (e.g.
    // the "Clear" chip in SundayAutoMode) mutates it. Without this the
    // grid would keep showing cards dimmed even after the user clears.
    const onExclChange = () => setExcluded(loadExcluded());
    window.addEventListener("wp:pool-refreshed", onPoolRefreshed);
    window.addEventListener("wp:excluded-games", onExclChange);
    return () => {
      window.removeEventListener("wp:pool-refreshed", onPoolRefreshed);
      window.removeEventListener("wp:excluded-games", onExclChange);
    };
  }, []);

  const toggleGame = (gameId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      saveExcluded(next);
      return next;
    });
  };

  // Sprint 60.96 — one-click reset. Re-locks every game so the full
  // slate re-enters the ILP pool without the user hunting for the
  // toggle on every dimmed card.
  const lockAll = () => {
    setExcluded(() => {
      const next = new Set();
      saveExcluded(next);
      return next;
    });
  };

  // Also let power users unlock everything at once — useful when they
  // want to isolate a single game.
  const unlockAll = () => {
    setExcluded(() => {
      const next = new Set(games.map((g) => g.game_id).filter(Boolean));
      saveExcluded(next);
      return next;
    });
  };

  // Sprint 60.96 — Kickoff-time presets (Mass-Market Content Tier parity).
  // Windows are in ET wall-clock (NFL scheduling is ET-native):
  //   early     : kickoff <  15:00 ET  (the 1:00 PM window)
  //   afternoon : 15:00–18:59 ET      (the 4:05 / 4:25 window)
  //   primetime : 19:00+ ET            (SNF / MNF / TNF / Sat night)
  // Presets set the exclusion set to "every game NOT in the chosen
  // window", so the same `wp_excluded_games` state powers everything
  // downstream (Slate Intel banners, Optimizer excluded_games payload).
  const _hourET = (iso) => {
    if (!iso) return null;
    try {
      // Use `en-US` + `America/New_York` so DST is respected.
      const parts = new Intl.DateTimeFormat("en-US", {
        hour: "numeric", hour12: false, timeZone: "America/New_York",
      }).formatToParts(new Date(iso));
      const h = parts.find((p) => p.type === "hour");
      return h ? Number(h.value) : null;
    } catch { return null; }
  };
  const _matchesWindow = (g, window) => {
    const h = _hourET(g.kickoff);
    if (h === null) return false;
    if (window === "early") return h < 15;
    if (window === "afternoon") return h >= 15 && h < 19;
    if (window === "primetime") return h >= 19;
    return true;
  };
  const applyKickoffPreset = (window) => {
    setExcluded(() => {
      // Exclude every game that does NOT match the chosen window.
      const next = new Set(
        games
          .filter((g) => g.game_id && !_matchesWindow(g, window))
          .map((g) => g.game_id),
      );
      saveExcluded(next);
      return next;
    });
  };
  // Count games in each window so we can hide preset chips that would
  // exclude the entire slate (i.e. no games match).
  const windowCounts = useMemo(() => ({
    early: games.filter((g) => _matchesWindow(g, "early")).length,
    afternoon: games.filter((g) => _matchesWindow(g, "afternoon")).length,
    primetime: games.filter((g) => _matchesWindow(g, "primetime")).length,
  }), [games]);
  // Detect which preset (if any) matches the CURRENT exclusion set so
  // we can highlight it as active. A preset is active when the set of
  // excluded games equals the complement of the window's included games.
  const activePreset = useMemo(() => {
    if (excluded.size === 0) return null;
    for (const w of ["early", "afternoon", "primetime"]) {
      const expected = new Set(
        games
          .filter((g) => g.game_id && !_matchesWindow(g, w))
          .map((g) => g.game_id),
      );
      if (expected.size === excluded.size &&
          [...expected].every((id) => excluded.has(id))) {
        return w;
      }
    }
    return null;
  }, [excluded, games]);

  const includedCount = games.filter((g) => !excluded.has(g.game_id)).length;
  const vegasCovered = payload?.vegas_covered || 0;
  const isDkOnly = payload?.source === "dk_only";

  return (
    <section className="bg-[#121212] border border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Broadcast size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading font-bold">Games ({includedCount}/{games.length})</div>
          {isDkOnly && games.length > 0 && (
            <div className="overline text-amber-500/80 ml-2">
              Vegas syncing — showing DK matchups
            </div>
          )}
          {vegasCovered > 0 && (
            <div className="overline text-zinc-500 ml-2">
              {vegasCovered}/{games.length} with live odds
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sprint 60.96 — Kickoff-time preset chips. Only render a
              chip when at least one game in the current slate lands in
              that window (a Thursday-Only slate would omit Early). */}
          {games.length > 1 && (
            <div className="flex items-center gap-1 border-r border-zinc-800 pr-2 mr-1" data-testid="kickoff-presets">
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-600 mr-0.5">Kickoff:</span>
              {[
                { key: "early", label: "Early", title: "1:00 PM ET games only (kickoff < 3PM ET)" },
                { key: "afternoon", label: "Afternoon", title: "4:00 PM ET games only (kickoff 3–7PM ET)" },
                { key: "primetime", label: "Primetime", title: "SNF / MNF / TNF (kickoff 7PM ET+)" },
              ].filter((w) => windowCounts[w.key] > 0).map((w) => {
                const active = activePreset === w.key;
                return (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => applyKickoffPreset(w.key)}
                    title={w.title}
                    data-testid={`kickoff-preset-${w.key}`}
                    className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                      active
                        ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                    }`}
                  >
                    {w.label} <span className="opacity-60">({windowCounts[w.key]})</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Sprint 60.96 — bulk lock/unlock controls. Only show them when
              the current state has at least one game the button would
              actually change (skip Lock-All when everything's already
              locked, skip Unlock-All when everything's already unlocked). */}
          {excluded.size > 0 && (
            <button
              type="button"
              data-testid="game-lock-all"
              onClick={lockAll}
              title="Re-lock every game (default state — full slate active)"
              className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors"
            >
              Lock All
            </button>
          )}
          {games.length > 0 && excluded.size < games.length && (
            <button
              type="button"
              data-testid="game-unlock-all"
              onClick={unlockAll}
              title="Unlock every game (excludes all players from the ILP)"
              className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white transition-colors"
            >
              Unlock All
            </button>
          )}
          <div className="overline text-zinc-500">Click a game to lock/unlock it</div>
        </div>
      </div>

      <div className="p-3">
        {loading && games.length === 0 && (
          <div className="text-xs mono text-zinc-500 py-8 text-center">Loading slate games…</div>
        )}
        {!loading && games.length === 0 && (
          <div className="text-xs mono text-zinc-500 py-8 text-center">
            No slate loaded — pick a slate from the Slate Selection tab first.
          </div>
        )}
        {games.length > 0 && (
          <div
            data-testid="game-card-grid"
            className="grid gap-2"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {games.map((g) => (
              <GameCard
                key={g.game_id}
                game={g}
                excluded={excluded.has(g.game_id)}
                onToggle={() => toggleGame(g.game_id)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
